package twist.mena.sports.admin.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import twist.mena.sports.admin.api.AdminApiClient
import twist.mena.sports.admin.state.UiState
import twist.mena.sports.admin.theme.*

@Composable
fun LoginScreen(apiClient: AdminApiClient) {
    val colors = AppTheme.colors
    val isDark = AppTheme.isDark
    var email by remember { mutableStateOf("") }
    var state by remember { mutableStateOf<UiState<String>>(UiState.Success("")) }
    val scope = rememberCoroutineScope()

    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            minHeight(100.vh)
            backgroundColor(Color(colors.background))
            themeTransition()
        }
    }) {
        Div({
            style {
                cardStyle(colors)
                width(400.px)
                textAlign("center")
            }
        }) {
            Img(src = "twist-header-logo.svg", alt = "Twist Sports") {
                style {
                    width(160.px)
                    marginBottom(Spacing.lg)
                    property("object-fit", "contain")
                }
            }

            P({
                style { color(Color(colors.textMuted)); marginBottom(Spacing.xxl); fontSize(Typography.lg) }
            }) { Text("Admin Dashboard") }

            when (val s = state) {
                is UiState.Loading -> {
                    P({ style { color(Color(colors.textMuted)) } }) { Text("Sending...") }
                }
                is UiState.Error -> {
                    Div({
                        style {
                            padding(Spacing.md)
                            backgroundColor(Color(if (isDark) "#450a0a" else "#fee2e2"))
                            color(Color(if (isDark) "#fca5a5" else "#b91c1c"))
                            borderRadius(BorderRadius.md)
                            marginBottom(Spacing.lg)
                        }
                    }) { Text(s.message) }
                }
                is UiState.Success -> {
                    if (s.data.isNotEmpty()) {
                        Div({
                            style {
                                padding(Spacing.xl)
                                backgroundColor(Color(if (isDark) "#052e16" else "#d1fae5"))
                                color(Color(if (isDark) "#86efac" else "#065f46"))
                                borderRadius(BorderRadius.md)
                                marginBottom(Spacing.lg)
                            }
                        }) {
                            P({ style { fontWeight("600"); marginBottom(Spacing.sm) } }) {
                                Text("Login link sent!")
                            }
                            P({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                                Text("Check your email and click the link to sign in")
                            }
                        }

                        // Email client buttons
                        val sentEmail = email.lowercase()
                        if (sentEmail.endsWith("@twistmena.com")) {
                            A(href = "https://webmail.mail.eu-west-1.awsapps.com/workmail/?organization=twistmena") {
                                Button({
                                    style {
                                        width(100.percent)
                                        primaryButtonStyle(colors)
                                        marginBottom(Spacing.md)
                                    }
                                }) { Text("Open Twist Mail") }
                            }
                        } else if (sentEmail.endsWith("@etisalat.com") || sentEmail.endsWith("@eand.com.eg")) {
                            A(href = "https://mail.etisalat.com") {
                                Button({
                                    style {
                                        width(100.percent)
                                        primaryButtonStyle(colors)
                                        marginBottom(Spacing.md)
                                    }
                                }) { Text("Open e& Mail") }
                            }
                        }

                    }
                }
            }

            if (state !is UiState.Success || (state as UiState.Success).data.isEmpty()) {
                Input(type = InputType.Email) {
                    value(email)
                    placeholder("Email address")
                    onInput { email = it.value }
                    style {
                        formInputStyle(colors)
                        marginBottom(Spacing.lg)
                    }
                }

                Button({
                    onClick {
                        if (email.isNotBlank()) {
                            state = UiState.Loading
                            scope.launch {
                                try {
                                    apiClient.requestMagicLink(email)
                                    state = UiState.Success("sent")
                                } catch (e: AdminApiClient.AdminNotFoundException) {
                                    state = UiState.Error("No admin account found. Please contact Amina to create an admin account.")
                                } catch (e: AdminApiClient.ServerErrorException) {
                                    state = UiState.Error("Server is down. Please contact Amina to open staging.")
                                } catch (e: Exception) {
                                    state = UiState.Error("Something went wrong. Please try again.")
                                }
                            }
                        }
                    }
                    style {
                        width(100.percent)
                        primaryButtonStyle(colors)
                    }
                }) { Text("Send Login Link") }
            }
        }
    }
}
